import { IconButton, TextArea } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC, ReactElement } from "react";
import { useState } from "react";
import { Form } from "../../../../components/form/form";
import { Input } from "../../../../components/input/input";
import { Toggle } from "../../../../components/toggle/toggle";
import { useFactory } from "../../../../helpers/reactHooks";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { FormSectionType } from "../../obPaymentPageForm";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import styles from "./aboutSection.module.scss";

type AboutFields = Pick<
  PaymentPageForm,
  "paymentPageDescription" | "businessType" | "switchedOnAbout" | "switchedOnBusinessType"
>;
// Both the page form and the modal form are rendered by renderForm
type AboutForm = Pick<Form<AboutFields>, "getValue" | "onChange">;

export const AboutSection: FC<FormSectionType> = observer(({ form, store }) => {
  const { paymentPageDescription, businessType, switchedOnAbout, switchedOnBusinessType } = form.values;
  const modalForm = useFactory(
    () => new Form<AboutFields>({ paymentPageDescription, businessType, switchedOnAbout, switchedOnBusinessType }),
  );
  const [isFilled, setFilled] = useState(!!form.getValue("paymentPageDescription"));

  const [editMode, setEditMode] = useState(false);

  const handleChangeToggle = (value: boolean, formData: AboutForm): void => {
    formData.onChange("switchedOnAbout", value);
    if (!value) {
      formData.onChange("paymentPageDescription", "");
      formData.onChange("businessType", "");
    }
  };

  const handleChangeToggleBusinessType = (value: boolean): void => {
    modalForm.onChange("switchedOnBusinessType", value);
    if (!value) {
      modalForm.onChange("businessType", "");
    }
  };

  const renderForm = (formData: AboutForm): ReactElement => (
    <div className={classNames(styles.section, styles.withSeparator)}>
      <div className={styles.aboutSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionSubtitle}>About us</span>
            <span className={styles.sectionDescription}>
              Include a short description about your business or services
            </span>
          </div>
          <Toggle
            label="About us"
            value={formData.getValue("switchedOnAbout")}
            onChange={(value: boolean) => {
              handleChangeToggle(value, formData);
            }}
          />
        </div>

        {formData.getValue("switchedOnAbout") && (
          <TextArea
            name="paymentPageDescription"
            className={classNames(styles.fieldContainer, styles.textareaField)}
            textSize="l"
            placeholder="e.g. The Happiness Company has been spreading smiles since 1999"
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              formData.onChange("paymentPageDescription", e.target.value);
            }}
            value={formData.getValue("paymentPageDescription") ?? undefined}
          />
        )}
      </div>
    </div>
  );

  const renderBusinessTypeFormSection = (): ReactElement => (
    <div className={styles.section}>
      <div className={styles.aboutSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionSubtitle}>Business type</span>
            <span className={styles.sectionDescription}>
              Add the type of business do you have e.g. Gentlemen Barbers
            </span>
          </div>
          <Toggle
            label="Business type"
            value={modalForm.getValue("switchedOnBusinessType")}
            onChange={handleChangeToggleBusinessType}
          />
        </div>

        {modalForm.getValue("switchedOnBusinessType") && (
          <Input
            label="Business type"
            className={styles.fieldContainer}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              modalForm.onChange("businessType", e.target.value);
            }}
            value={modalForm.getValue("businessType") ?? undefined}
          />
        )}
      </div>
    </div>
  );

  const renderViewContent = (): ReactElement => (
    <div className={styles.viewSection}>
      <div className={styles.aboutSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionViewTitle}>
            <span className={styles.sectionSubtitle}>About</span>
          </div>
          <IconButton
            title="Edit about us"
            icon="actionEdit"
            iconColor="themeGreen"
            size="s"
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>

        <span className={styles.paymentPageDescription}>{paymentPageDescription}</span>
      </div>
    </div>
  );

  if (store.firstUse || !isFilled) {
    return renderForm(form);
  }

  const handleCancelEditModal = (): void => {
    setEditMode(false);
    modalForm.onMultipleChange([
      { field: "paymentPageDescription", value: form.getValue("paymentPageDescription") },
      { field: "businessType", value: form.getValue("businessType") },
      { field: "switchedOnAbout", value: form.getValue("switchedOnAbout") },
    ]);
  };

  const handleSaveEditModal = (): void => {
    setEditMode(false);
    if (modalForm.getValue("paymentPageDescription")) {
      setFilled(true);
    } else {
      form.onChange("switchedOnAbout", false);
      setFilled(false);
    }
    form.onMultipleChange([
      { field: "paymentPageDescription", value: modalForm.getValue("paymentPageDescription") },
      { field: "businessType", value: modalForm.getValue("businessType") },
      { field: "switchedOnAbout", value: modalForm.getValue("switchedOnAbout") },
    ]);
  };

  return (
    <>
      {renderViewContent()}
      <EditFormModalSection open={editMode} onCancel={handleCancelEditModal} onSave={handleSaveEditModal}>
        <div>
          {renderForm(modalForm)}
          {renderBusinessTypeFormSection()}
        </div>
      </EditFormModalSection>
    </>
  );
});
